import * as _ from "lodash";
import { DimensionType, IHierarchy, IHierarchyLevel, IHierarchyNode } from "../PA.Core/PA.Core.IF.Hierarchy";
import { WB_CustomerHierarchyRow, WB_CustomerRelationshipRow, WB_CustomerRow } from "../PA.Core/PAdbSchemaRows";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { IMessageProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import {
    esWB_Customer_Hierarchy,
    esWB_Customer_Hierarchy_Level,
    esWB_Customer_Relationship,
} from "../PA.Core/PAdbSchema";
import { ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { HierarchyBuilderService } from "./HierarchyBuilderService";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { LoadHierarchyOptions } from "./PA.MD.Services.IF";

export interface CreateOptions {
    bFullHierarchy?: boolean;
    bUseLegacyLevel?: boolean
}

export class CustomerHierarchy implements IHierarchy {
    private _wbCustHId: number;
    private _CustHId: string;
    private _CustHName: string;
    private _effectiveStartDt: Date;
    private _effectiveEndDt: Date;
    private dbContext: PAdbContext;

    private dctHierarchyLevels: { [LevelId: string]: IHierarchyLevel };
    private dctNodes: { [NodeId: string]: IHierarchyNode };
    private dctNodesByLevel: { [LevelId: string]: IHierarchyNode[] };

    private dctNodeParentByChildId: { [NodeId: string]: IHierarchyNode };
    private dctNodeChildrenByParentId: { [NodeId: string]: IHierarchyNode[] };
    private arrTopLevelNodes: IHierarchyNode[];

    private _loadMessageProvider: MessageProvider;
    private _OnLoadComplete: Promise<void>;
    private _bLoaded: boolean;
    private _bUseHierarchyBuilder: boolean;
    private _bFullHierarchy: boolean;
    private _bUseLegacyLevel: boolean

    constructor(wbCustHId: number, EffectiveStartDate: Date, EffectiveEndDate: Date, options?: CreateOptions) {
        this.dbContext = new PAdbContext();
        this._wbCustHId = wbCustHId;
        this._CustHId = "Not Constructed";
        this._CustHName = "Not Constructed";
        this._effectiveStartDt = EffectiveStartDate;
        this._effectiveEndDt = EffectiveEndDate;
        this._bLoaded = false;
        this._loadMessageProvider = new MessageProvider();
        this.dctHierarchyLevels = {};
        this.dctNodes = {};
        this.dctNodesByLevel = {};
        this.dctNodeParentByChildId = {};
        this.dctNodeChildrenByParentId = {};
        this.arrTopLevelNodes = [];
        this._bUseHierarchyBuilder = true;

        const opts = options || {}
        this._bFullHierarchy = !!options?.bFullHierarchy;

        if(_.isBoolean(opts.bUseLegacyLevel))
            this._bUseLegacyLevel = opts.bUseLegacyLevel
        else
            this._bUseLegacyLevel = false

    }

    get DimensionType(): DimensionType {
        return DimensionType.Customer;
    }

    get HierarchyId(): string {
        return this._wbCustHId + "";
    }

    get HierarchyIdUI(): string {
        return this._CustHId;
    }

    get HierarchyName(): string {
        return this._CustHName;
    }

    get bHasSortOrder(): boolean {
        return true;
    }

    get LoadMessageProvider(): IMessageProvider {
        return this._loadMessageProvider;
    }

    get bLoaded(): boolean {
        return this._bLoaded;
    }

    get OnLoadComplete(): Promise<void> {
        const self = this;

        if (self._bLoaded) return Promise.resolve();

        if (!_.isUndefined(self._OnLoadComplete)) return self._OnLoadComplete;

        let returnValue: Promise<void>;

        if (self._bUseHierarchyBuilder) returnValue = self._OnLoadComplete = self.OnLoadComplete_UseHierarchyBuilder;
        else returnValue = self._OnLoadComplete = self.OnLoadComplete_UseJSData;

        return returnValue;
    }

    private get OnLoadComplete_UseJSData(): Promise<void> {
        const self = this;

        const returnValue = (self._OnLoadComplete = (async () => {
            let msg = "";
            const recordResolver = new ESRecordResolver(self.dbContext, {
                standardConditions: {
                    [esWB_Customer_Hierarchy_Level]: { DELETED_IND: { nin: ["1"] } },
                    [esWB_Customer_Relationship]: { DELETED_IND: { nin: ["1"] } },
                },
            });

            self._loadMessageProvider.clearMessages();

            // Get the Hierarchy Record
            await recordResolver.findAllWithRelationships(esWB_Customer_Hierarchy, [self._wbCustHId]);
            const aCustHRow: WB_CustomerHierarchyRow = <any>(
                recordResolver.get(esWB_Customer_Hierarchy, self._wbCustHId)
            );

            if (_.isEmpty(aCustHRow)) {
                msg = `Unknown Customer Hierarchy ${self._wbCustHId}`;
                self.LoadMessageProvider.addMessage({
                    code: "MissingCustH",
                    type: MessageType.Error,
                    title: msg,
                    description: msg,
                });
                return;
            }

            self._CustHId = aCustHRow.cif.WB_CUST_HIER_ID;
            self._CustHName = aCustHRow.cif.WB_CUST_HIER_NAME;

            // Populate Levels from MetaData
            _.forEach(
                _.filter(aCustHRow.WB_CustomerHierarchyLevelRows, (aRow) => aRow.cif.DELETED_IND != "1"),
                (aLevelRow) => {
                    if (aLevelRow.cif.LEVEL_ID in self.dctHierarchyLevels) {
                        msg = `Customer Hierarchy Level (${aLevelRow.cif.LEVEL_ID}) duplicated`;
                        self.LoadMessageProvider.addMessage({
                            code: "DupCustHLvl",
                            type: MessageType.Warning,
                            title: msg,
                            description: msg,
                        });
                        return; // Next Level
                    }

                    self.dctHierarchyLevels[aLevelRow.cif.LEVEL_ID] = {
                        LevelId: aLevelRow.cif.LEVEL_ID,
                        LevelName: aLevelRow.cif.LEVEL_NAME,
                        LevelDepth: aLevelRow.cif.LEVEL_DEPTH,
                    };
                },
            );

            // Read in the Relationship and Nodes
            let arrRelRows: WB_CustomerRelationshipRow[] = <any>await recordResolver.queryAllWithRelationships(
                esWB_Customer_Relationship,
                {
                    where: {
                        WB_CUSTH_ID: this._wbCustHId,
                        DELETED_IND: { nin: ["1"] },
                    },
                },
            );

            //-- Sort by Child, StartDt, Key to make the results repeatable
            arrRelRows = _.sortBy(arrRelRows, ["WB_CUST_ID", "EFFECTIVE_START_DT", "WB_CUSTR_ID"]);

            _.forEach(arrRelRows, (aRow) => {
                //--  Check if relation in valid period
                if (
                    !DateUtils.RangeDatesOverlap(
                        self._effectiveStartDt,
                        self._effectiveEndDt,
                        aRow.EffectiveStartDt,
                        aRow.EffectiveEndDt,
                    )
                )
                    return;

                //-- Only look at rows that have valid parents and children
                if (_.isEmpty(aRow.WB_CustomerRowParent) || _.isEmpty(aRow.WB_CustomerRowChild)) return;

                const parent = self.addNode(aRow.WB_CustomerRowParent);
                const child = self.addNode(aRow.WB_CustomerRowChild);
                self.addHierarchyChildParentRel(child, parent);
            });

            // Determine Top Level Nodes, nodes without Parents.
            _.forEach(this.dctNodes, (aNode, nodeId) => {
                if (!("ParentId" in aNode)) this.arrTopLevelNodes.push(aNode);
            });

            if(!self._bUseLegacyLevel)
                self.resetNodeLevelIdUsingDepth()

            // Find Levels In Nodes but not in Hierarchy
            _.forEach(this.dctNodesByLevel, (arrNodes, levelId) => {
                if (levelId in this.dctHierarchyLevels) return;

                // Create missing Level
                const newLevel: IHierarchyLevel = {
                    LevelId: levelId,
                    LevelName: levelId,
                    LevelDepth: 0,
                };

                this.dctHierarchyLevels[levelId] = newLevel;
            });

            self._bLoaded = true;
        })());

        return returnValue;
    }

    private get OnLoadComplete_UseHierarchyBuilder(): Promise<void> {
        const self = this;
        const returnValue = (self._OnLoadComplete = (async () => {
            let msg = "";

            const userContext = PAEnvironment.getValue(PAEnvironment.UserContext);
            const wbUserId = userContext.User.cif.WB_AUTHUSR_ID;

            self._loadMessageProvider.clearMessages();

            const hb = new HierarchyBuilderService();
            const lo: LoadHierarchyOptions = {
                EffectiveStartDt: self._effectiveStartDt,
                EffectiveEndDt: self._effectiveEndDt,
            };
            if (!self._bFullHierarchy) lo.UserId = wbUserId;

            const hbResponse = await hb.getHierarchyComponents(DimensionType.Customer, self._wbCustHId, lo);

            // Get the Hierarchy Record

            self._CustHId = hbResponse.HierarchyIdUI;
            self._CustHName = hbResponse.HierarchyName;
            if (_.isEmpty(self._CustHName)) self._CustHName = hbResponse.HierarchyName;

            // Populate Levels from MetaData
            _.forEach(hbResponse.Levels, (aLevel) => {
                if (aLevel.LevelId in self.dctHierarchyLevels) {
                    msg = `Customer Hierarchy Level (${aLevel.LevelId}) duplicated`;
                    self.LoadMessageProvider.addMessage({
                        code: "DupCustHLvl",
                        type: MessageType.Warning,
                        title: msg,
                        description: msg,
                    });
                    return; // Next Level
                }

                self.dctHierarchyLevels[aLevel.LevelId] = aLevel;
            });

            // Add Nodes to main dictionary and Level Array
            _.forEach(hbResponse.Nodes, (aNode) => {
                self.dctNodes[aNode.NodeId] = aNode;
                if (aNode.NodeLevel in this.dctNodesByLevel) this.dctNodesByLevel[aNode.NodeLevel].push(aNode);
                else this.dctNodesByLevel[aNode.NodeLevel] = [aNode];

                if ("ParentId" in aNode) {
                    if (aNode.ParentId in this.dctNodeChildrenByParentId)
                        this.dctNodeChildrenByParentId[aNode.ParentId].push(aNode);
                    else this.dctNodeChildrenByParentId[aNode.ParentId] = [aNode];
                }
            });

            // Do a second pass to find the parents
            _.forEach(hbResponse.Nodes, (aNode) => {
                if (!("ParentId" in aNode)) return;

                const parentId = aNode.ParentId;
                const parent = this.dctNodes[parentId];
                if (parent) this.dctNodeParentByChildId[aNode.NodeId] = parent;
            });

            // Determine Top Level Nodes, nodes without Parents.
            _.forEach(this.dctNodes, (aNode, nodeId) => {
                if (!("ParentId" in aNode)) this.arrTopLevelNodes.push(aNode);
            });


            if(!self._bUseLegacyLevel)
                self.resetNodeLevelIdUsingDepth()

            // Find Levels In Nodes but not in Hierarchy
            _.forEach(this.dctNodesByLevel, (arrNodes, levelId) => {
                if (levelId in this.dctHierarchyLevels) return;

                // Create missing Level
                const newLevel: IHierarchyLevel = {
                    LevelId: levelId,
                    LevelName: levelId,
                    LevelDepth: 0,
                };

                this.dctHierarchyLevels[levelId] = newLevel;
            });


            self._bLoaded = true;
        })());

        return returnValue;
    }

    private resetNodeLevelIdUsingDepth(){
        const self = this

        self.dctNodesByLevel = {}

        // Reset Levels
        _.forEach(_.values(self.dctNodes), (n) => {
            // @ts-expect-error "Readonly"
            n["NodeLevel"] = ""
        })

        let dctLevelsByDepth = _.keyBy(self.dctHierarchyLevels, l => l.LevelDepth)

        function setNodeLevelByDepth(aNode: IHierarchyNode, depth: number){
            if(depth in dctLevelsByDepth){
                let lvl = dctLevelsByDepth[depth]
                // @ts-expect-error "Readonly
                aNode["NodeLevel"] = lvl.LevelId
            } else {
                // @ts-expect-error "Readonly"
                aNode["NodeLevel"] = '' + depth
            }
            // Set children depth recursively
            for(let child of (self.dctNodeChildrenByParentId[aNode.NodeId] || [])){
                setNodeLevelByDepth(child, depth + 1)
            }
        };

        _.forEach(self.arrTopLevelNodes, (node) => {
            setNodeLevelByDepth(node, 1)
        })

        _.forEach(_.values(self.dctNodes), (n) => {
            if(n.NodeLevel in self.dctNodesByLevel)
                self.dctNodesByLevel[n.NodeLevel].push(n)
            else
                self.dctNodesByLevel[n.NodeLevel] = [n]
        });
    }

    private addNode(aCustRow: WB_CustomerRow): IHierarchyNode {
        //--
        if (_.isEmpty(aCustRow)) return undefined;

        const wbCustId = aCustRow.cif.WB_CUST_ID + "";
        const levelId = aCustRow.cif.CUSTOMER_TYPE;

        if (wbCustId in this.dctNodes) return this.dctNodes[wbCustId];

        const newNode: IHierarchyNode = {
            NodeId: wbCustId,
            NodeIdUI: aCustRow.cif.CUSTOMER_ID,
            NodeName: aCustRow.cif.CUSTOMER_NAME,
            NodeLevel: aCustRow.cif.CUSTOMER_TYPE,
            NodeOrder: aCustRow.cif.CUSTOMER_NAME,
            NodeShortName: aCustRow.cif.CUSTOMER_NAME,
        };

        this.dctNodes[wbCustId] = newNode;

        if (levelId in this.dctNodesByLevel) this.dctNodesByLevel[levelId].push(newNode);
        else this.dctNodesByLevel[levelId] = [newNode];

        return this.dctNodes[wbCustId];
    }

    private addHierarchyChildParentRel(aChild: IHierarchyNode, aParent: IHierarchyNode): void {
        const nodeIdChild = aChild.NodeId;
        const nodeIdParent = aParent.NodeId;

        if (nodeIdChild in this.dctNodeParentByChildId) {
            const msg = `Child ${aChild.NodeName} (${aChild.NodeId}) has duplicate Parent ${aParent?.NodeName} (${aParent?.NodeIdUI})`;
            this._loadMessageProvider.addMessage({
                code: "DuplicateParent",
                type: MessageType.Warning,
                title: msg,
                description: msg,
            });
            return;
        }

        this.dctNodeParentByChildId[nodeIdChild] = aParent;
        aChild.ParentId = aParent.NodeId;

        if (nodeIdParent in this.dctNodeChildrenByParentId) this.dctNodeChildrenByParentId[nodeIdParent].push(aChild);
        else this.dctNodeChildrenByParentId[nodeIdParent] = [aChild];
    }

    getLevels(): IHierarchyLevel[] {
        return _.values(this.dctHierarchyLevels);
    }

    getTopLevelNodes(): IHierarchyNode[] {
        return this.arrTopLevelNodes;
    }

    getNode(NodeId: string): IHierarchyNode {
        if (NodeId in this.dctNodes) return this.dctNodes[NodeId];

        return undefined;
    }

    getNodes(): IHierarchyNode[] {
        return _.values(this.dctNodes);
    }

    getNodesAtLevel(HierarchyLevel: string): IHierarchyNode[] {
        if (HierarchyLevel in this.dctNodesByLevel) return this.dctNodesByLevel[HierarchyLevel];

        return [];
    }

    getChildren(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string, levelId?: string): IHierarchyNode[] {
        const arrNodeIds = this.convertToKeyArray(nodes);

        const bCheckLevel = !_.isEmpty(levelId);
        const returnValue: IHierarchyNode[] = [];

        const arrRecurseOnChildren: IHierarchyNode[] = [];
        _.forEach(arrNodeIds, (aNodeId) => {
            if (aNodeId in this.dctNodeChildrenByParentId) {
                const arrChildren = this.dctNodeChildrenByParentId[aNodeId];
                _.forEach(arrChildren, (aChildNode) => {
                    if (bCheckLevel) {
                        if (levelId == aChildNode.NodeLevel) returnValue.push(aChildNode);
                        else arrRecurseOnChildren.push(aChildNode);
                    } else {
                        returnValue.push(aChildNode);
                    }
                });
            }
        });

        // Recurse
        if (bCheckLevel && !_.isEmpty(arrRecurseOnChildren)) {
            const moreChildren = this.getChildren(arrRecurseOnChildren, levelId);
            _.forEach(moreChildren, (a) => returnValue.push(a));
        }

        return returnValue;
    }

    getParents(
        nodes: IHierarchyNode[] | IHierarchyNode | string[] | string,
        levelId?: string,
    ): {
        [NodeId: string]: IHierarchyNode;
    } {
        const arrNodeIds = this.convertToKeyArray(nodes);

        const bCheckLevel = !_.isEmpty(levelId);
        const returnValue: { [NodeId: string]: IHierarchyNode } = {};

        const heightLimit = 20;
        _.forEach(arrNodeIds, (aNodeId) => {
            let depth = 0;
            let parent = this.dctNodeParentByChildId[aNodeId];
            if (bCheckLevel) {
                while (parent && parent?.NodeLevel != levelId && depth < heightLimit) {
                    parent = this.dctNodeParentByChildId[parent.NodeId];
                    depth += 1;
                }
                if (depth == heightLimit) parent = undefined;
            }

            returnValue[aNodeId] = parent;
        });

        return returnValue;
    }

    private convertToKeyArray(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string): string[] {
        const returnValue: string[] = [];

        if (_.isEmpty(nodes) && !_.isNumber(nodes)) return returnValue;

        let keyList: any[];
        if (_.isArray(nodes)) keyList = nodes;
        else keyList = [nodes];

        _.forEach(keyList, (aKeyItem) => {
            if (_.isString(aKeyItem)) {
                returnValue.push(aKeyItem);
                return;
            }

            if (_.isNumber(aKeyItem)) returnValue.push(aKeyItem + "");

            if (_.isEmpty(aKeyItem)) return;

            if ("NodeId" in aKeyItem) returnValue.push(aKeyItem.NodeId);
        });

        return returnValue;
    }
}
